import React, { useState } from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { PageHeader } from '../../../components/layout/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/FormField';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { EntityActionMenu } from '../../../components/ui/EntityActionMenu';
import { useToast } from '../../../components/ui/Toast';
import { EditLeadModal } from '../components/EditLeadModal';
import {
  useCustomer360,
  useLead,
  useLogCrmActivity,
  useDeleteLead,
  useStartLeadProgress,
  useDisqualifyLead,
} from '../hooks';
import { Edit, Trash2, ArrowRightCircle, CheckCircle } from 'lucide-react';

export function ContactDetailPage() {
  const params = useParams({ strict: false }) as { id?: string };
  const targetId = params.id || '';
  const navigate = useNavigate();
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<'overview' | 'activities' | 'interests' | 'consents'>('overview');
  const [newActivityNote, setNewActivityNote] = useState('');
  const [activityType, setActivityType] = useState('NOTE');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { data: leadData, refetch: refetchLead } = useLead(targetId);
  const effectivePartyId = leadData?.partyId || targetId;

  const { data: customer360, refetch: refetch360 } = useCustomer360(effectivePartyId);
  const logActivityMutation = useLogCrmActivity(effectivePartyId);
  const deleteMutation = useDeleteLead();
  const startProgressMutation = useStartLeadProgress(leadData?.id ?? '');
  const disqualifyMutation = useDisqualifyLead(leadData?.id ?? '');

  const activities = (customer360?.activities && customer360.activities.length > 0)
    ? customer360.activities
    : (customer360?.recentActivities ?? []);
  const consents = (customer360?.consents && customer360.consents.length > 0)
    ? customer360.consents
    : (customer360?.activeConsents ?? []);

  const handleLogActivity = async () => {
    if (!newActivityNote.trim() || !effectivePartyId) return;
    try {
      await logActivityMutation.mutateAsync({
        partyId: effectivePartyId,
        type: activityType,
        notes: newActivityNote,
      });
      setNewActivityNote('');
      void refetch360();
      toast({ type: 'success', title: 'Activity logged' });
    } catch (err: unknown) {
      console.error('Failed to log activity:', err);
      const msg = err instanceof Error ? err.message : 'Failed to log activity';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const handleDeleteLead = async () => {
    if (!leadData?.id) return;
    try {
      await deleteMutation.mutateAsync({ id: leadData.id });
      toast({ type: 'success', title: 'Lead Deleted', description: 'Lead successfully deleted and archived.' });
      setIsDeleteOpen(false);
      void navigate({ to: '/crm/leads' });
    } catch (err: unknown) {
      console.error('Failed to delete lead:', err);
      const msg = err instanceof Error ? err.message : 'Failed to delete lead';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const handleStartProgress = async () => {
    try {
      await startProgressMutation.mutateAsync();
      toast({ type: 'success', title: 'Progress Started', description: 'Lead moved to In Progress.' });
      void refetchLead();
    } catch (err: unknown) {
      console.error('Failed to start progress:', err);
      const msg = err instanceof Error ? err.message : 'Failed to start progress';
      toast({ type: 'error', title: 'Error', description: msg });
    }
  };

  const renderHeaderActions = () => {
    return (
      <div className="flex items-center gap-2">
        {leadData && (
          <Button variant="secondary" size="sm" onClick={() => setIsEditOpen(true)}>
            <Edit className="w-3.5 h-3.5 mr-1.5" />
            Edit Lead
          </Button>
        )}

        {leadData?.status === 'NEW' && (
          <Button size="sm" onClick={() => void handleStartProgress()}>
            <ArrowRightCircle className="w-3.5 h-3.5 mr-1.5" />
            Start Progress
          </Button>
        )}

        {leadData?.status === 'QUALIFIED' && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => void navigate({ to: '/customers/new', search: { contactId: leadData.id } })}
          >
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
            Enroll as Customer
          </Button>
        )}

        {leadData && (
          <EntityActionMenu
            actions={[
              {
                label: 'Edit Lead Details',
                icon: <Edit className="w-4 h-4" />,
                onClick: () => setIsEditOpen(true),
              },
              {
                label: 'Disqualify Lead',
                variant: 'destructive',
                requiresConfirmation: true,
                confirmTitle: 'Disqualify Lead',
                confirmDescription: 'Are you sure you want to disqualify this lead?',
                confirmLabel: 'Disqualify',
                hidden: leadData.status === 'DISQUALIFIED' || leadData.status === 'CONVERTED',
                onClick: async () => {
                  try {
                    await disqualifyMutation.mutateAsync({ reason: 'NOT_INTERESTED' });
                    toast({ type: 'success', title: 'Lead Disqualified' });
                    void refetchLead();
                  } catch (err: unknown) {
                    console.error('Failed to disqualify lead:', err);
                    toast({ type: 'error', title: 'Error', description: 'Failed to disqualify lead' });
                  }
                },
              },
              {
                label: 'Delete Lead',
                icon: <Trash2 className="w-4 h-4" />,
                variant: 'destructive',
                requiresConfirmation: true,
                confirmTitle: 'Delete Lead',
                confirmDescription: 'Are you sure you want to delete this lead? This will soft delete the lead record and remove it from active pipelines.',
                onClick: () => void handleDeleteLead(),
              },
            ]}
          />
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full p-6 space-y-6" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title={customer360?.name || customer360?.party?.canonicalName || leadData?.partyName || `Lead / Customer 360 (${effectivePartyId.slice(0, 8)})`}
        subtitle="Unified 360 view of leads, product interests, interactions, and consents"
        backTo="/crm/leads"
        action={renderHeaderActions()}
      />

      {/* Tabs */}
      <div className="flex space-x-3 border-b border-border-base pb-2">
        {(['overview', 'activities', 'interests', 'consents'] as const).map((tab) => (
          <Button
            key={tab}
            variant={activeTab === tab ? 'primary' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab)}
            className="capitalize"
          >
            {tab}
          </Button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="col-span-1">
            <CardHeader><CardTitle className="text-base">Party Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div><span className="text-text-muted">ID:</span> <span className="font-mono text-xs">{effectivePartyId}</span></div>
              <div><span className="text-text-muted">Name:</span> <span className="font-medium">{customer360?.name || customer360?.party?.canonicalName || leadData?.partyName || 'N/A'}</span></div>
              <div><span className="text-text-muted">Phone:</span> <span>{customer360?.phone || customer360?.party?.primaryMobile || leadData?.partyMobile || 'N/A'}</span></div>
              <div><span className="text-text-muted">Email:</span> <span>{customer360?.email || customer360?.party?.email || 'N/A'}</span></div>
              {leadData?.source && (
                <div><span className="text-text-muted">Source:</span> <span className="font-medium">{leadData.source}</span></div>
              )}
              {leadData?.status && (
                <div><span className="text-text-muted">Lead Status:</span> <span className="font-semibold text-accent-600">{leadData.status}</span></div>
              )}
            </CardContent>
          </Card>

          <Card className="col-span-2">
            <CardHeader><CardTitle className="text-base">Quick Interaction Log</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex space-x-2">
                <select
                  value={activityType}
                  onChange={(e) => setActivityType(e.target.value)}
                  className="p-2 border border-border-base rounded-md text-sm bg-surface-1"
                >
                  <option value="NOTE">Note</option>
                  <option value="CALL">Call</option>
                  <option value="MEETING">Meeting</option>
                  <option value="WHATSAPP">WhatsApp</option>
                </select>
                <Input
                  type="text"
                  value={newActivityNote}
                  onChange={(e) => setNewActivityNote(e.target.value)}
                  placeholder="Log an interaction or summary..."
                  className="flex-1"
                />
                <Button onClick={() => void handleLogActivity()} disabled={!newActivityNote.trim()}>
                  Log
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'activities' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Interaction Timeline</CardTitle></CardHeader>
          <CardContent>
            {activities.length > 0 ? (
              <div className="space-y-4">
                {activities.map((act) => (
                  <div key={act.id} className="p-3 border border-border-base rounded-md text-sm space-y-1">
                    <div className="flex justify-between text-xs text-text-muted">
                      <span className="font-semibold text-accent-600">{act.type}</span>
                      <span>{new Date(act.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="text-text-primary">{act.notes || 'No notes'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted py-4 text-center">No recorded activities for this party.</p>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'interests' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Product Interests</CardTitle></CardHeader>
          <CardContent>
            {customer360?.productInterests && customer360.productInterests.length > 0 ? (
              <div className="space-y-3">
                {customer360.productInterests.map((pi) => (
                  <div key={pi.id} className="p-3 border border-border-base rounded-md flex justify-between items-center text-sm">
                    <div>
                      <p className="font-medium text-text-primary">{pi.productName || pi.productInstanceId}</p>
                      <p className="text-xs text-text-muted">Source: {pi.source}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs bg-surface-subtle font-medium">{pi.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted py-4 text-center">No product interests recorded.</p>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'consents' && (
        <Card>
          <CardHeader><CardTitle className="text-base">Consent Records</CardTitle></CardHeader>
          <CardContent>
            {consents.length > 0 ? (
              <div className="space-y-3">
                {consents.map((c) => (
                  <div key={c.id} className="p-3 border border-border-base rounded-md flex justify-between items-center text-sm">
                    <div>
                      <p className="font-medium text-text-primary">{c.purpose} ({c.scope})</p>
                      <p className="text-xs text-text-muted">Source: {c.source} | Granted: {new Date(c.grantedAt).toLocaleDateString()}</p>
                    </div>
                    <span className="px-2 py-0.5 rounded text-xs bg-success-bg text-success-text font-medium">{c.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-text-muted py-4 text-center">No active consents found.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Modal */}
      {isEditOpen && leadData && (
        <EditLeadModal
          lead={leadData}
          open={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            void refetchLead();
            void refetch360();
          }}
        />
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={isDeleteOpen}
        title="Delete Lead"
        description="Are you sure you want to delete this lead? This will soft delete the lead record and remove it from active pipelines."
        confirmLabel="Delete Lead"
        onConfirm={() => void handleDeleteLead()}
        onCancel={() => setIsDeleteOpen(false)}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
